import { useMemo } from "react";

import { useGetRequest as useGetExternalRequest, useListAttachmentDescriptions } from "@synthesis/shared-services/external/application/hooks";
import { list1 as listExternalFiles } from "@synthesis/shared-services/files/external/client";
import type { FileMetaDto } from "@synthesis/shared-services/files/external/types";
import {
  useGetRequest as useGetInternalRequest,
  useListAttachmentDescriptions as useListInternalAttachmentDescriptions,
} from "@synthesis/shared-services/internal/application/hooks";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";

import { useClientType } from "../../../shared/hooks/use-client-type";
import { buildExternalApplicationDetail, buildInternalApplicationDetail, type ApplicationDetail } from "../application-detail-page.lib";
import { useApplicationDictionariesContext } from "../application-dictionaries-context";
import { useFinalDecisionDraft, useFinalDecisionRefusal } from "./use-final-decision";

const EXTERNAL_FILE_METAS_QUERY_KEY = (id: string) => ["external-file-metas", id] as const;

export const useApplicationDetail = () => {
  const { id } = useParams({ from: "/applications/$id" });
  const clientType = useClientType();
  const { isLoading: isDictionariesLoading } = useApplicationDictionariesContext();

  const shouldLoadExternal = clientType === "external";
  const shouldLoadInternal = clientType === "internal";

  const externalRequestQuery = useGetExternalRequest({ path: { id: id ?? "" } }, { query: { enabled: shouldLoadExternal } });
  const externalAttachmentsQuery = useListAttachmentDescriptions({ path: { id: id ?? "" } }, { query: { enabled: shouldLoadExternal } });
  const internalRequestQuery = useGetInternalRequest({ path: { id: id ?? "" } }, { query: { enabled: shouldLoadInternal } });
  const internalAttachmentsQuery = useListInternalAttachmentDescriptions({ path: { id: id ?? "" } }, { query: { enabled: shouldLoadInternal } });

  // Черновик итогового решения есть только в статусах «Формируется решение» / «Решение на доработке»:
  // вне них GET /final-decision отвечает 409, поэтому query вне этих статусов не запускаем.
  const internalStatus = internalRequestQuery.data?.status;
  const canHaveFinalDecisionDraft = internalStatus === "DECISION_FORMING" || internalStatus === "DECISION_REWORK";
  const finalDecisionDraft = useFinalDecisionDraft(id ?? "", { enabled: shouldLoadInternal && canHaveFinalDecisionDraft });
  const canHaveFinalDecisionRefusal = internalStatus === "REJECTED";
  const finalDecisionRefusal = useFinalDecisionRefusal(id ?? "", { enabled: shouldLoadInternal && canHaveFinalDecisionRefusal });

  const shouldFetchFileMetas = shouldLoadExternal && Boolean(id) && !externalAttachmentsQuery.isLoading;

  const fileMetasQuery = useQuery({
    queryKey: EXTERNAL_FILE_METAS_QUERY_KEY(id ?? ""),
    queryFn: async ({ signal }) => (await listExternalFiles({ body: { id: id ?? "" }, signal })).data,
    enabled: shouldFetchFileMetas,
  });

  const application = useMemo((): ApplicationDetail | null => {
    if (clientType === "external" && externalRequestQuery.data) {
      const externalFileMetas: FileMetaDto[] =
        fileMetasQuery.data?.map((item) => item.fileMeta).filter((fileMeta): fileMeta is FileMetaDto => Boolean(fileMeta)) ?? [];
      return buildExternalApplicationDetail(externalRequestQuery.data, externalAttachmentsQuery.data ?? [], externalFileMetas);
    }

    if (clientType === "internal" && internalRequestQuery.data) {
      return buildInternalApplicationDetail(
        internalRequestQuery.data,
        internalAttachmentsQuery.data ?? [],
        finalDecisionDraft.draft,
        finalDecisionRefusal.refusal,
      );
    }

    return null;
  }, [
    clientType,
    externalRequestQuery.data,
    externalAttachmentsQuery.data,
    fileMetasQuery.data,
    internalRequestQuery.data,
    internalAttachmentsQuery.data,
    finalDecisionDraft.draft,
    finalDecisionRefusal.refusal,
  ]);

  const isLoading =
    !clientType ||
    isDictionariesLoading ||
    (shouldLoadExternal && (externalRequestQuery.isLoading || externalAttachmentsQuery.isLoading || fileMetasQuery.isLoading)) ||
    (shouldLoadInternal &&
      (internalRequestQuery.isLoading || internalAttachmentsQuery.isLoading || finalDecisionDraft.isLoading || finalDecisionRefusal.isLoading));

  const isError =
    (shouldLoadExternal && (externalRequestQuery.isError || externalAttachmentsQuery.isError)) ||
    (shouldLoadInternal && (internalRequestQuery.isError || internalAttachmentsQuery.isError));

  return {
    application,
    isLoading,
    isError,
    isFinalDecisionDraftAccessDenied: finalDecisionDraft.isDraftAccessDenied,
  };
};
